import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, X, Clock, Infinity, FlaskConical, CheckCircle2 } from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';

const PlusStatusModal = ({ isOpen, onClose, statusData }) => {
  const { t, i18n } = useTranslation('shared');
  const dateLocale = getDateFnsLocale(i18n.language);
  if (typeof document === 'undefined') return null;

  const { plan, source, expiresAt, remainingDays, isTrial, isLifetime } = statusData || {};

  const isAdmin = plan === 'admin';

  const renderBadge = () => {
    if (isAdmin) {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#3d8ef0]/20 text-[#3d8ef0] border border-[#3d8ef0]/30">
          {t('plusStatusModal.adminBadge')}
        </span>
      );
    }
    if (source === 'admin') {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
          {t('plusStatusModal.adminPlusBadge')}
        </span>
      );
    }
    if (isTrial) {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-500/20 text-amber-400 border border-amber-500/30">
          {t('plusStatusModal.trialBadge')}
        </span>
      );
    }
    return (
      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
        {t('plusStatusModal.activePlusBadge')}
      </span>
    );
  };

  const renderExpiry = () => {
    if (isAdmin || isLifetime) {
      return (
        <div className="flex items-center gap-2 text-emerald-400">
          <Infinity className="w-5 h-5" />
          <span className="text-sm font-medium">{t('plusStatusModal.lifetimeAccess')}</span>
        </div>
      );
    }
    if (expiresAt && remainingDays !== null && remainingDays !== undefined) {
      const formatted = format(new Date(expiresAt), 'd MMMM yyyy', { locale: dateLocale });
      const urgentClass = remainingDays <= 3
        ? 'text-red-400'
        : remainingDays <= 7
        ? 'text-amber-400'
        : 'text-[#c8d8eb]';
      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${urgentClass}`} />
            <span className={`text-sm font-semibold ${urgentClass}`}>
              {t('plusStatusModal.daysLeft', { count: remainingDays })}
            </span>
          </div>
          <p className="text-xs text-[#4d6a8a] pl-6">{t('plusStatusModal.expiresOn', { date: formatted })}</p>
          {source === 'admin' && (
            <p className="text-xs text-amber-400/80 pl-6">{t('plusStatusModal.definedByAdmin')}</p>
          )}
          {remainingDays <= 7 && source !== 'admin' && (
            <p className="text-xs text-amber-400/80 pl-6">
              {t('plusStatusModal.extendHint')}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[99000] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          <motion.div
            className="relative w-full max-w-sm bg-[#0d1424] border border-white/[0.08] rounded-2xl shadow-[0_32px_80px_rgba(0,0,0,0.7)] overflow-hidden"
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ duration: 0.2 }}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-lg text-[#4d6a8a] hover:text-[#c8d8eb] hover:bg-white/[0.06] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center flex-shrink-0">
                  {isTrial ? (
                    <FlaskConical className="w-5 h-5 text-amber-400" />
                  ) : (
                    <Crown className="w-5 h-5 text-amber-400" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-0.5">
                    <h2 className="text-base font-bold text-white">{t('plusStatusModal.title')}</h2>
                    {renderBadge()}
                  </div>
                  <p className="text-xs text-[#4d6a8a]">
                    {isTrial ? t('plusStatusModal.trialActive') : t('plusStatusModal.membershipActive')}
                  </p>
                </div>
              </div>
            </div>

            {/* Süre bilgisi */}
            <div className="px-6 py-4 border-b border-white/[0.06]">
              {renderExpiry()}
            </div>

            {/* Aktif özellikler */}
            <div className="px-6 py-4">
              <p className="text-xs text-[#4d6a8a] font-semibold uppercase tracking-wider mb-3">
                {t('plusStatusModal.activeFeaturesHeading')}
              </p>
              <div className="space-y-2">
                {[
                  t('plusStatusModal.features.upload'),
                  t('plusStatusModal.features.collabEdit'),
                  t('plusStatusModal.features.groups'),
                  t('plusStatusModal.features.sounds'),
                ].map((f) => (
                  <div key={f} className="flex items-center gap-2.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span className="text-sm text-[#c8d8eb]">{f}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="px-6 pb-5">
              <button
                onClick={onClose}
                className="w-full py-2.5 rounded-xl bg-[#1e2d47] text-[#c8d8eb] text-sm font-medium hover:bg-[#24354f] transition-colors"
              >
                {t('plusStatusModal.ok')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default PlusStatusModal;
